import { useStartMeeting } from '@actions';
import { ReactComponent as LogoIcon } from '@assets/icons/logo-no-text.svg';
import Button from '@components/V4/Button';
import Modal from '@components/V4/Modal/Modal';
import { useAnalytics } from '@contexts/AnalyticsContext';
import { useAuthState } from '@contexts/AuthContext';
import { ZoomContext } from '@modules/zoom/contexts/ZoomContext';
import { getWaitingScreenMeetingDetails } from '@shared/apis/video.apis';
import { MeetingStatus } from '@shared/enums';
import { documentUrl } from '@shared/utils/string';
import useToast from 'apps/agora/src/hooks/useToast';
import { ANALYTICS_EVENT_NAMES } from 'apps/agora/src/utils/constants';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams, useLocation, useHistory } from 'react-router-dom';

type Props = {
  setIsWaitingRoom: (isWaitingRoom: boolean) => void;
};

const VideoMeetingDetails = (props: Props) => {
  const { setIsWaitingRoom } = props;

  const [showMeetingWarningModal, setShowMeetingWarningModal] = useState(false);
  const [warningType, setWarningType] = useState<'scheduledBefore' | 'startLater' | null>(null);

  const { trackEvent } = useAnalytics();

  const { isMentor } = useAuthState();

  const queryClient = useQueryClient();

  const {
    meetingDetails,
    zoomClient,
    canJoinMeeting,
    isZoomClientReady,
    isSocketConnected,
    sendRoomEvent,
    setIsMeetingLoading,
  } = useContext(ZoomContext);

  const location = useLocation();
  const history = useHistory();

  const toast = useToast();

  const { meetingId } = useParams<{ meetingId: string }>();

  const { name, scheduledDate, status, agenda, googleDrive, areMeetingsScheduledBefore } =
    meetingDetails || {};

  const { mutate: startMeeting } = useStartMeeting(meetingId || '', {
    onSuccess: () => {
      queryClient.invalidateQueries(getWaitingScreenMeetingDetails.endpoint);
    },
  });

  useEffect(() => {
    if (!isSocketConnected) return;

    sendRoomEvent('join-waiting-room', meetingId);

    return () => {
      sendRoomEvent('leave-waiting-room', meetingId);
    };
  }, [isSocketConnected, meetingId]);

  const joinMeeting = async () => {
    if (!meetingDetails) return;

    setIsMeetingLoading(true);

    try {
      await zoomClient?.join(
        meetingDetails.accessTokenData.session_name,
        meetingDetails.accessToken,
        meetingDetails.accessTokenData.name
      );

      trackEvent(ANALYTICS_EVENT_NAMES.MEETING_JOIN);

      setIsWaitingRoom(false);

      if (status === MeetingStatus.SCHEDULED) {
        startMeeting({});
      }

      const params = new URLSearchParams(location.search);
      params.append('inMeeting', 'true');

      history.replace({
        pathname: location.pathname,
        search: params.toString(),
      });
    } catch (error: any) {
      trackEvent(ANALYTICS_EVENT_NAMES.MEETING_JOIN_FAIL, error.reason);

      toast.error(error.reason);
    }

    setIsMeetingLoading(false);
  };

  const handleJoinMeeting = async () => {
    if (!meetingDetails) return;

    const scheduledMoment = moment(meetingDetails.scheduledDate);
    const oneHourBefore = scheduledMoment.clone().subtract(1, 'hour');
    const doesMeetingStartLater = moment().isBefore(oneHourBefore);

    if (areMeetingsScheduledBefore) {
      setWarningType('scheduledBefore');
      setShowMeetingWarningModal(true);
      return;
    }
    if (meetingDetails.status === MeetingStatus.SCHEDULED && doesMeetingStartLater) {
      setWarningType('startLater');
      setShowMeetingWarningModal(true);
      return;
    }

    await joinMeeting();
  };

  return (
    <div
      className="
          flex flex-col gap-6 items-center px-4 pb-6
          tablet:px-0 tablet:order-1
          laptop:items-start laptop:w-1/2 laptop:min-w-1/2 laptop:max-w-[calc(50%-clamp(4rem,7.2vw,6.5rem)/2)]
          largeLaptop:max-w-[29rem]
        "
    >
      <LogoIcon />

      <h1 className="font-raleway text-xl font-bold">{name}</h1>

      <p className="text-xs font-semibold leading-5">
        Date & Time:{' '}
        <span className="font-normal">{moment(scheduledDate).format('DD MMM YYYY, HH:mm')}</span>
      </p>

      {!!agenda && (
        <p className="text-xs text-center font-semibold leading-5 laptop:text-left">
          Meeting Agenda: <span className="font-normal">{agenda}</span>
        </p>
      )}

      <div className="flex justify-between gap-4">
        <Button
          disabled={!canJoinMeeting || !isZoomClientReady}
          variant="primary"
          buttonText="Join Meeting"
          className="h-8"
          onClick={handleJoinMeeting}
        />
        {googleDrive && (
          <a
            href={`https://drive.google.com/drive/folders/${googleDrive}`}
            target="_blank"
            rel="noreferrer"
          >
            <Button
              variant="secondary"
              buttonText={isMentor ? 'Open Student Drive' : 'Open Drive'}
              className="h-8"
            />
          </a>
        )}
        {meetingDetails?.homeworkFileId && (
          <a href={documentUrl(meetingDetails.homeworkFileId)} target="_blank" rel="noreferrer">
            <Button variant="secondary" buttonText="View Homework" className="h-8" />
          </a>
        )}
      </div>

      {showMeetingWarningModal && (
        <Modal size="small" isOpen={true} onClose={() => setShowMeetingWarningModal(false)}>
          <Modal.Header title="Warning" />
          <Modal.Body className="flex flex-col justify-center items-center text-center">
            <p className="pb-6 text-white">
              {warningType === 'scheduledBefore'
                ? 'There are earlier scheduled meetings for this enrollment. Do you still want to join?'
                : 'This meeting is scheduled more than an hour from now. Do you still want to join?'}
            </p>
          </Modal.Body>
          <Modal.Footer className="flex justify-end gap-4">
            <Button
              variant="secondary"
              buttonText="Cancel"
              className="h-8"
              onClick={() => setShowMeetingWarningModal(false)}
            />
            <Button
              variant="primary"
              buttonText="Join Anyway"
              className="h-8"
              onClick={joinMeeting}
            />
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default VideoMeetingDetails;
